import React, { useEffect } from 'react';
import { useComparison } from '../../stores/useComparisonStore';
import { useComparisonStore } from '../../stores/useComparisonStore';
import { useDateRange } from '../../stores/useDateRangeStore';
import { useComparisonData } from '../../hooks/useComparisonData';
import { DailyProfitSummary } from '../../api/profit';
import { 
  compareMetrics, 
  generatePerformanceSummary, 
  MetricComparison,
  calculatePreviousPeriod,
  calculateSamePeriodLastYear
} from '../../utils/comparisonUtils';
import { formatDateRangeDisplay } from '../../utils/dateUtils';
import StandaloneSection from './StandaloneSection';
import './ComparisonTable.css';

/**
 * Format a value based on its type
 */
const formatValue = (value: number, formatType: 'currency' | 'percentage' | 'count'): string => {
  switch (formatType) {
    case 'currency':
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case 'percentage':
      return `${value.toFixed(1)}%`;
    case 'count':
      return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
    default:
      return value.toString();
  }
};

/**
 * Format a change value with appropriate sign and formatting
 */
const formatChange = (value: number, formatType: 'currency' | 'percentage' | 'count'): string => {
  const sign = value > 0 ? '+' : (value < 0 ? '-' : '');
  const absValue = Math.abs(value);

  if (formatType === 'percentage') {
    // For percentage metrics, show percentage points (pp)
    return `${sign}${absValue.toFixed(1)}pp`;
  }
  
  if (formatType === 'currency') {
    return `${sign}$${absValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  // For count
  return `${sign}${absValue.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

/**
 * Get verdict icon
 * For normal metrics: better = up-right (↗), worse = down-right (↘)
 * For inverted metrics (like fee ratio): better = down-right (↘), worse = up-right (↗)
 *
 * Color is always based on verdict (green=better, red=worse)
 * Arrow direction considers invertLogic (for metrics where lower is better)
 */
const VerdictIcon: React.FC<{ verdict: 'better' | 'same' | 'worse'; invertLogic: boolean }> = ({ verdict, invertLogic }) => {
  if (verdict === 'same') {
    return <span className="verdict-icon same" aria-label="Same">→</span>;
  }

  // Color class is always based on verdict
  const colorClass = verdict === 'better' ? 'better' : 'worse';

  // For inverted logic metrics, flip the arrow direction
  const showUpArrow = invertLogic ? verdict === 'worse' : verdict === 'better';
  const arrow = showUpArrow ? '↗' : '↘';

  return <span className={`verdict-icon ${colorClass}`} aria-label={verdict}>{arrow}</span>;
};

/**
 * Get color class for change value
 */
const getChangeColorClass = (comparison: MetricComparison): string => {
  if (comparison.verdict === 'better') return 'change-better';
  if (comparison.verdict === 'worse') return 'change-worse';
  return 'change-same';
};

/**
 * Comparison Table Component
 * 
 * Displays side-by-side comparison of key metrics between current and comparison periods.
 * Shows absolute and percentage changes, plus verdict indicators.
 */

interface ComparisonTableProps {
  currentData: DailyProfitSummary[];
}

const ComparisonTable: React.FC<ComparisonTableProps> = ({ currentData }) => {
  const { active, range: comparisonRange, type } = useComparison();
  const { dateRange: currentRange, rangeType } = useDateRange();
  const { data: comparisonData, loading, error } = useComparisonData();
  const setComparisonType = useComparisonStore((state) => state.setComparisonType);
  const setComparison = useComparisonStore((state) => state.setComparison);

  // Calculate and set the comparison range when current range or type changes
  useEffect(() => {
    if (!active || !type || (type !== 'previous' && type !== 'lastYear')) {
      return;
    }

    // Calculate the appropriate comparison range
    let calculatedRange;
    if (type === 'previous') {
      calculatedRange = calculatePreviousPeriod(currentRange);
    } else if (type === 'lastYear') {
      calculatedRange = calculateSamePeriodLastYear(currentRange);
    }

    if (calculatedRange) {
      setComparison(type, calculatedRange);
    }
  }, [currentRange.start_date, currentRange.end_date, type, active, setComparison]);

  // Don't render if no current data
  if (!currentData || currentData.length === 0) {
    return null;
  }

  // Don't render if comparison is not active or invalid type
  if (!active || !type || (type !== 'previous' && type !== 'lastYear')) {
    return null;
  }

  const handleToggle = (newType: 'previous' | 'lastYear') => {
    if (newType !== type) {
      setComparisonType(newType);
    }
  };

  // Show loading state
  if (loading || !comparisonRange) {
    return (
        <StandaloneSection title="COMPARATIVE ANALYSIS">
          <div className="comparison-tabs">
            <button
              className={`tab-button ${type === 'previous' ? 'active' : ''}`}
              onClick={() => handleToggle('previous')}
              disabled={loading}
            >
              Previous Period
            </button>
            <button
              className={`tab-button ${type === 'lastYear' ? 'active' : ''}`}
              onClick={() => handleToggle('lastYear')}
              disabled={loading}
            >
              Year Over Year
            </button>
          </div>

          <div className="comparison-loading">
            <div className="loading-spinner" />
            <p>Loading comparison data...</p>
          </div>
        </StandaloneSection>
    );
  }

  // Show error state
  if (error) {
    return (
        <StandaloneSection title="COMPARATIVE ANALYSIS">
          <div className="comparison-tabs">
            <button
              className={`tab-button ${type === 'previous' ? 'active' : ''}`}
              onClick={() => handleToggle('previous')}
              disabled={loading}
            >
              Previous Period
            </button>
            <button
              className={`tab-button ${type === 'lastYear' ? 'active' : ''}`}
              onClick={() => handleToggle('lastYear')}
              disabled={loading}
            >
              Year Over Year
            </button>
          </div>

          <div className="comparison-error">
            <p className="error-message">⚠️ Unable to load comparison data</p>
            <p className="error-detail">{error}</p>
          </div>
        </StandaloneSection>
    );
  }

  // Show empty state
  if (!comparisonData || comparisonData.length === 0) {
    return (
        <StandaloneSection title="COMPARATIVE ANALYSIS">
          <div className="comparison-tabs">
            <button
              className={`tab-button ${type === 'previous' ? 'active' : ''}`}
              onClick={() => handleToggle('previous')}
              disabled={loading}
            >
              Previous Period
            </button>
            <button
              className={`tab-button ${type === 'lastYear' ? 'active' : ''}`}
              onClick={() => handleToggle('lastYear')}
              disabled={loading}
            >
              Year Over Year
            </button>
          </div>

          <div className="comparison-empty">
            <p className="empty-message">📊 No trading activity during comparison period</p>
            <p className="empty-detail">
              {formatDateRangeDisplay(comparisonRange, 'custom')}
            </p>
          </div>
        </StandaloneSection>
    );
  }

  // Calculate comparisons using the provided current data
  const comparisons = compareMetrics(currentData, comparisonData);
  const summary = generatePerformanceSummary(comparisons);

  return (
      <StandaloneSection title="COMPARATIVE ANALYSIS">
        <div className="comparison-tabs">
          <button
            className={`tab-button ${type === 'previous' ? 'active' : ''}`}
            onClick={() => handleToggle('previous')}
            disabled={loading}
          >
            Previous Period
          </button>
          <button
            className={`tab-button ${type === 'lastYear' ? 'active' : ''}`}
            onClick={() => handleToggle('lastYear')}
            disabled={loading}
          >
            Year Over Year
          </button>
        </div>

        <div className="period-labels">
          <span className="period-label comparison">
            {type === 'previous' && 'Previous Period: '}
            {type === 'lastYear' && 'Same Period Last Year: '}
            {formatDateRangeDisplay(comparisonRange, 'custom')}
          </span>
          <span className="period-separator">vs.</span>
          <span className="period-label current">
            Current: {formatDateRangeDisplay(currentRange, rangeType)}
          </span>
        </div>

        <div className="comparison-table-wrapper">
          <table className="comparison-table" role="table" aria-label="Metric comparison table">
            <thead>
              <tr>
                <th scope="col">Metric</th>
                <th scope="col" className="verdict-column">Status</th>
                <th scope="col">Prior</th>
                <th scope="col">Current</th>
                <th scope="col">Change</th>
                <th scope="col">%</th>
              </tr>
            </thead>
            <tbody>
              {comparisons.map((comparison) => (
                <tr key={comparison.name} className="comparison-row">
                  <td className="comparison-metric-name">{comparison.name}</td>
                  <td className="verdict-cell">
                    <VerdictIcon verdict={comparison.verdict} invertLogic={comparison.invertLogic} />
                  </td>
                  <td className="metric-value comparison-value">
                    {formatValue(comparison.comparison, comparison.formatType)}
                  </td>
                  <td className="metric-value current-value">
                    {formatValue(comparison.current, comparison.formatType)}
                  </td>
                  <td className={`change-value ${getChangeColorClass(comparison)}`}>
                    {formatChange(comparison.absoluteChange, comparison.formatType)}
                  </td>
                  <td className={`percent-value ${getChangeColorClass(comparison)}`}>
                    {comparison.percentageChange >= 0 ? '+' : ''}
                    {comparison.percentageChange.toFixed(1)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Summary */}
        <div className="comparison-summary">
          <div className="summary-content">
            <p className="summary-headline">{summary.split('\n')[0]}</p>
            {summary.split('\n')[1] && (
              <p className="summary-detail">{summary.split('\n')[1]}</p>
            )}
          </div>
        </div>
      </StandaloneSection>
  );


};

export default ComparisonTable;
